/** A dense table that owns its own loading and empty states. */

import type { CSSProperties, ReactNode } from "react";

import { cn } from "@/lib/cn";

import { EmptyState } from "../EmptyState/EmptyState";
import { Skeleton } from "../Skeleton/Skeleton";
import { lengthOf } from "../internal/style";
import "../internal/base.css";
import "./DataTable.css";

const SKELETON_ROWS = 4;

export interface DataTableColumn<Row> {
  readonly key: string;
  readonly header: ReactNode;
  readonly align?: "left" | "right" | "center";
  readonly width?: string | number;
  /** The identifying column, and only that one. */
  readonly emphasis?: boolean;
  readonly mono?: boolean;
  readonly numeric?: boolean;
  readonly muted?: boolean;
  readonly render?: (row: Row) => ReactNode;
}

export interface DataTableProps<Row> {
  readonly columns: readonly DataTableColumn<Row>[];
  readonly rows: readonly Row[];
  /** dense is a 40px row, comfortable is 52px. */
  readonly density?: "dense" | "comfortable";
  /** A skeleton at the table's own shape, never a spinner. */
  readonly loading?: boolean;
  readonly emptyHeadline?: ReactNode;
  readonly emptyBody?: ReactNode;
  readonly emptyAction?: ReactNode;
  /** The table's name, rendered for a screen reader only. */
  readonly caption: string;
  readonly className?: string;
  readonly style?: CSSProperties;
}

function cellText(value: unknown): ReactNode {
  if (typeof value === "string" || typeof value === "number") return value;
  if (typeof value === "boolean") return String(value);
  return null;
}

function keyOf(row: unknown, index: number): string | number {
  const id = (row as Record<string, unknown>)["id"];
  return typeof id === "string" || typeof id === "number" ? id : index;
}

export function DataTable<Row>({
  columns,
  rows,
  density = "dense",
  loading = false,
  emptyHeadline = "Nothing yet",
  emptyBody,
  emptyAction,
  caption,
  className,
  style,
}: DataTableProps<Row>) {
  const empty = !loading && rows.length === 0;

  return (
    <div className={cn("al-table-wrap", className)} style={style}>
      <table className={cn("al-table", `al-table-${density}`)}>
        <caption className="al-visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                style={column.width === undefined ? undefined : { width: lengthOf(column.width) }}
                className={cn(
                  column.align !== undefined ? `al-cell-${column.align}` : undefined,
                  column.numeric === true ? "al-tabular" : undefined,
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading
            ? Array.from({ length: SKELETON_ROWS }, (_unused, row) => (
                <tr key={`skeleton-${row}`}>
                  {columns.map((column) => (
                    <td key={column.key}>
                      <Skeleton height="var(--al-space-4)" />
                    </td>
                  ))}
                </tr>
              ))
            : rows.map((row, index) => (
                <tr key={keyOf(row, index)}>
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cn(
                        column.align !== undefined ? `al-cell-${column.align}` : undefined,
                        column.emphasis === true ? "al-cell-emphasis" : undefined,
                        column.muted === true ? "al-cell-muted" : undefined,
                        column.mono === true ? "al-mono" : undefined,
                        column.numeric === true ? "al-tabular" : undefined,
                      )}
                    >
                      {column.render
                        ? column.render(row)
                        : cellText((row as Record<string, unknown>)[column.key])}
                    </td>
                  ))}
                </tr>
              ))}
          {empty ? (
            <tr>
              <td colSpan={Math.max(1, columns.length)}>
                <EmptyState
                  bare
                  headline={emptyHeadline}
                  {...(emptyBody === undefined ? {} : { body: emptyBody })}
                  {...(emptyAction === undefined ? {} : { action: emptyAction })}
                />
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
